// v0.5.0g+4 — KL-1 stale-exclusion banner (closes the final KL-1
// contract item — point 5 of the planned fix in
// /app/memory/KNOWN_LIMITATIONS.md).
//
// Surfaces the count of open registry entries older than `stale_days`
// (default 30) so the operator can never miss an unresolved drop
// sitting forever. Renders nothing when stale_count is 0 — this is
// "warn loudly, stay quiet when clean" by design. Polls
// `/api/admin/excluded-txns/staleness-summary` once per Dashboard
// mount; cheap because the underlying collection is indexed on
// `status`.
//
// Clicking the banner takes the operator straight to
// `/admin/excluded-backlog` where they can resolve each entry.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Warning, Clock } from "@phosphor-icons/react";
import { getStaleExclusionSummary } from "../lib/api";
import { useTranslation } from "react-i18next";

const _daysAgo = (iso) => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
};

export const StaleExclusionBanner = () => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getStaleExclusionSummary();
        if (!cancelled) setSummary(data);
      } catch (_e) {
        // 401/403 = non-admin viewer; banner stays hidden. Same
        // shape as the ReconciliationBanner — no surface, no
        // operator-facing noise on auth boundary.
        if (!cancelled) setSummary(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!summary) return null;
  if ((summary.stale_count || 0) === 0) return null;

  const oldest = summary.oldest_open;
  const oldestDays = _daysAgo(oldest?.excluded_at);

  return (
    <button
      type="button"
      data-testid="stale-exclusion-banner"
      data-resolution-class="recordkeeping"
      data-resolution-endpoint="navigation"
      onClick={() => navigate("/admin/excluded-backlog")}
      className="w-full text-left border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 rounded-md px-4 py-3 tabular-nums text-xs text-amber-900 dark:text-amber-100 leading-snug hover:bg-amber-100 dark:hover:bg-amber-900/60 transition-colors flex items-start gap-3"
    >
      <Warning size={16} weight="bold" className="shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="font-bold mb-0.5">
          {t("ui.excluded_backlog.stale_title", { count: summary.stale_count, days: summary.stale_days, defaultValue: `${summary.stale_count} dropped transaction${summary.stale_count === 1 ? "" : "s"} still unresolved (older than ${summary.stale_days} days)` })}
        </div>
        <div className="opacity-90">
          {t("ui.excluded_backlog.stale_body", { defaultValue: "Transactions dropped from a filing must be resolved as either “filed in a later calculation” or “not a real sale (bad data)” — otherwise they fall out of the audit trail. Open the backlog to record each resolution." })}
        </div>
        {oldest && oldestDays !== null && (
          <div
            data-testid="stale-exclusion-banner-oldest"
            className="mt-1.5 flex items-center gap-1.5 text-[11px] opacity-80"
          >
            <Clock size={11} weight="bold" />
            {t("ui.excluded_backlog.stale_oldest", { days: oldestDays, defaultValue: `Oldest unresolved: ${oldestDays} days ago` })}
            {oldest.country ? ` · ${oldest.country}` : ""}
            {oldest.order_id ? t("ui.excluded_backlog.stale_order", { id: oldest.order_id, defaultValue: ` · order ${oldest.order_id}` }) : ""}
          </div>
        )}
      </div>
      <span
        className="shrink-0 tabular-nums text-[11px] font-bold underline decoration-dotted"
      >
        {t("ui.excluded_backlog.stale_open", { defaultValue: "Open backlog →" })}
      </span>
    </button>
  );
};

export default StaleExclusionBanner;
